/**
 * Страница «План» — дискавери-борд планировочного слоя (dflow).
 *
 *   - Борд гипотез: колонки Now / Next / Later (Горизонт), карточки с
 *     порогами «да/нет», риском Кагана, статусом и привязкой к Итерации.
 *   - Итерации: спринт-полосы с ёмкостью и фокусом.
 *   - Соответствия (Four Fits): четыре квадранта со статусами.
 *   - Bullseye: кольца каналов.
 *
 * Read-only, как весь web: правки — в редакторе/через агента. Клик по
 * любому имени — EntityCard (той же правой панелью, что на Canvas-view).
 */

import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
    Alert, Anchor, Badge, Box, Card, Container, Divider, Group, Loader,
    SimpleGrid, Stack, Text, Title, Tooltip,
} from '@mantine/core';
import { IconArrowLeft } from '@tabler/icons-react';
import type { PlanBoard, PlanBoardFit, PlanBoardHypothesis, PlanBoardIteration, Snapshot } from '@pflow/shared/v2';
import { loadSnapshot } from '../lib/snapshot-loader';
import { registerChatContext } from '../stores/chat-store';
import { EntityCard } from '../components/EntityCard';
import { HelpMenu } from '../components/HelpMenu';
import { PLAN_TOUR } from '../lib/tours';
import { useUiStore } from '../stores/ui-store';

const HORIZONS = [
    { key: 'сейчас', title: 'Сейчас (Now)' },
    { key: 'дальше', title: 'Дальше (Next)' },
    { key: 'потом',  title: 'Потом (Later)' },
] as const;

const HYP_STATUS_COLOR: Record<string, string> = {
    'бэклог':       'gray',
    'в_проверке':   'blue',
    'подтверждена': 'green',
    'опровергнута': 'red',
    'отложена':     'yellow',
};

const FIT_STATUS_COLOR: Record<string, string> = {
    'гипотеза':    'gray',
    'достигнут':   'green',
    'под_угрозой': 'yellow',
    'сломан':      'red',
};

const RING_COLOR: Record<string, string> = {
    'кандидат':   'gray',
    'тестируем':  'blue',
    'победитель': 'green',
    'отброшен':   'red',
};

const FIT_KIND_TITLE: Record<string, string> = {
    'продукт_рынок':  'Продукт × Рынок',
    'продукт_канал':  'Продукт × Канал',
    'канал_модель':   'Канал × Модель',
    'модель_рынок':   'Модель × Рынок',
};

export function PlanPage(): JSX.Element {
    const { owner, repo } = useParams();
    const [params] = useSearchParams();
    const ref  = params.get('ref')  ?? undefined;
    const path = params.get('path') ?? undefined;
    const backQuery = (() => {
        const q = new URLSearchParams();
        if (ref) q.set('ref', ref);
        if (path) q.set('path', path);
        const qs = q.toString();
        return qs ? '?' + qs : '';
    })();

    const snapshotQ = useQuery({
        queryKey: ['snapshot', owner, repo, ref, path],
        queryFn:  () => loadSnapshot({ owner: owner!, repo: repo!, ref, path }),
        enabled:  !!owner && !!repo,
        retry:    false,
    });

    // Контекст глобального чата (App-level): агент знает, что открыт План
    // и какая карточка сущности (подписка на ui-store триггерит ре-регистрацию).
    const cardEntity = useUiStore(s => s.cardEntity);
    useEffect(() => {
        if (owner && repo) {
            registerChatContext({
                owner, repo, gitRef: ref, path,
                pageContext: { view: 'plan', openEntity: cardEntity ?? undefined },
            });
        }
    });

    if (snapshotQ.isLoading) {
        return <Container py="xl"><Loader /></Container>;
    }
    if (snapshotQ.error || !snapshotQ.data) {
        return (
            <Container py="xl">
                <Alert color="red" title="Не удалось загрузить проект">
                    {(snapshotQ.error as Error | undefined)?.message ?? 'нет данных'}
                </Alert>
            </Container>
        );
    }

    const snapshot: Snapshot = snapshotQ.data;
    const plan = snapshot.plan;

    return (
        <Box>
            <PlanTopBar snapshot={snapshot} backQuery={backQuery} />
            <Container size="xl" py="md">
                {!plan ? (
                    <Alert color="gray" title="Планировочный слой пуст">
                        В проекте нет dflow-сущностей (План / Итерация / Гипотеза / Соответствие).
                        Заведи их в редакторе или через агента — см. воркбук validation-workbook.md.
                    </Alert>
                ) : (
                    <PlanBoardView plan={plan} />
                )}
            </Container>
            <EntityCard
                owner={owner!}
                repo={repo!}
                gitRef={ref}
                sha={snapshot.meta.sha}
                path={path}
            />
        </Box>
    );
}

function PlanTopBar({ snapshot, backQuery }: { snapshot: Snapshot; backQuery: string }): JSX.Element {
    const title = snapshot.unit?.name ?? `${snapshot.meta.owner}/${snapshot.meta.repo}`;
    return (
        <Box
            component="header"
            style={{
                position: 'sticky', top: 0, zIndex: 100,
                background: 'var(--mantine-color-body)',
                borderBottom: '1px solid var(--mantine-color-default-border)',
                padding: '12px 16px',
            }}
        >
            <Group gap="sm" wrap="nowrap" justify="space-between">
                <Group gap="sm" wrap="nowrap">
                    <Anchor component={Link} to={`/r/${snapshot.meta.owner}/${snapshot.meta.repo}${backQuery}`} c="dimmed" data-tour="back-to-narrative">
                        <Group gap={4} wrap="nowrap"><IconArrowLeft size={16} /><Text fz="sm">к нарративу</Text></Group>
                    </Anchor>
                    <Divider orientation="vertical" />
                    <Title order={4} style={{ lineHeight: 1 }}>{title} · План</Title>
                </Group>
                <HelpMenu tour={PLAN_TOUR} />
            </Group>
        </Box>
    );
}

// ─── Борд ────────────────────────────────────────────────────────────────

function PlanBoardView({ plan }: { plan: PlanBoard }): JSX.Element {
    return (
        <Stack gap="lg">
            {plan.plans.length > 0 && (
                <Stack gap="xs" data-tour="plan-plans">
                    {plan.plans.map(p => (
                        <Group key={p.name} gap="xs" wrap="wrap">
                            <EntityChip type="План" name={p.name} />
                            {p.status && <Badge variant="light" color={p.status === 'активный' ? 'green' : 'gray'}>{p.status}</Badge>}
                            {p.goals.map(g => <EntityChip key={g} type="Результат" name={g} prefix="🎯" />)}
                        </Group>
                    ))}
                </Stack>
            )}

            <SimpleGrid cols={{ base: 1, md: 3 }} spacing="md" data-tour="plan-horizons">
                {HORIZONS.map(h => (
                    <HorizonColumn
                        key={h.key}
                        title={h.title}
                        hypotheses={plan.hypotheses.filter(x => (x.horizon ?? 'потом') === h.key)}
                    />
                ))}
            </SimpleGrid>

            {plan.iterations.length > 0 && (
                <>
                    <Title order={5}>Итерации</Title>
                    <SimpleGrid cols={{ base: 1, md: 2 }} spacing="sm" data-tour="plan-iterations">
                        {plan.iterations.map(it => <IterationCard key={it.name} it={it} />)}
                    </SimpleGrid>
                </>
            )}

            {plan.fits.length > 0 && (
                <>
                    <Title order={5}>Соответствия — Four Fits</Title>
                    <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="sm" data-tour="plan-fits">
                        {plan.fits.map(f => <FitCard key={f.name} fit={f} />)}
                    </SimpleGrid>
                </>
            )}

            {plan.rings.length > 0 && (
                <>
                    <Title order={5}>Каналы — Bullseye</Title>
                    <Group gap="xs" wrap="wrap" data-tour="plan-bullseye">
                        {plan.rings.map(r => (
                            <Tooltip key={r.channel} label={r.canvas ? `Канвас «${r.canvas}»` : undefined} disabled={!r.canvas}>
                                <Badge
                                    variant="light"
                                    color={RING_COLOR[r.ring] ?? 'gray'}
                                    style={{ cursor: 'pointer', textTransform: 'none' }}
                                    onClick={undefined}
                                >
                                    <ChipOpener type="Канал" name={r.channel} label={`${r.channel} · ${r.ring}`} />
                                </Badge>
                            </Tooltip>
                        ))}
                    </Group>
                </>
            )}
        </Stack>
    );
}

function HorizonColumn({ title, hypotheses }: { title: string; hypotheses: PlanBoardHypothesis[] }): JSX.Element {
    return (
        <Stack gap="xs">
            <Group gap={6}>
                <Text fz="sm" fw={600} c="dimmed" tt="uppercase" style={{ letterSpacing: 1 }}>{title}</Text>
                <Text fz="xs" c="dimmed">({hypotheses.length})</Text>
            </Group>
            {hypotheses.length === 0 && <Text fz="xs" c="dimmed">пусто</Text>}
            {hypotheses.map(h => <HypothesisCard key={h.name} h={h} />)}
        </Stack>
    );
}

function HypothesisCard({ h }: { h: PlanBoardHypothesis }): JSX.Element {
    const openCard = useUiStore(s => s.openCard);
    return (
        <Card withBorder padding="sm" radius="md" data-tour="plan-hypothesis">
            <Stack gap={6}>
                <Group justify="space-between" wrap="nowrap" align="flex-start">
                    <Text
                        fz="sm" fw={600}
                        style={{ cursor: 'pointer' }}
                        onClick={() => openCard({ type: 'Гипотеза', name: h.name })}
                    >
                        {h.name}
                    </Text>
                    {h.status && (
                        <Badge size="sm" variant="light" color={HYP_STATUS_COLOR[h.status] ?? 'gray'} style={{ flexShrink: 0, textTransform: 'none' }}>
                            {h.status}
                        </Badge>
                    )}
                </Group>
                {h.statement && <Text fz="xs" c="dimmed" lineClamp={3}>{h.statement}</Text>}
                <Group gap={4} wrap="wrap">
                    {h.risk       && <Badge size="xs" variant="outline" color="grape" style={{ textTransform: 'none' }}>риск: {h.risk}</Badge>}
                    {h.confidence && <Badge size="xs" variant="outline" color="cyan"  style={{ textTransform: 'none' }}>{h.confidence}</Badge>}
                    {h.iteration  && <Badge size="xs" variant="outline" color="blue"  style={{ textTransform: 'none' }}>🏃 {h.iteration}</Badge>}
                </Group>
                {(h.confirmSignal || h.rejectSignal || h.expect) && (
                    <Stack gap={2}>
                        {h.confirmSignal && <Text fz="xs"><Text span c="green.7" fw={600}>да:</Text> {h.confirmSignal}</Text>}
                        {h.rejectSignal  && <Text fz="xs"><Text span c="red.7"   fw={600}>нет:</Text> {h.rejectSignal}</Text>}
                        {!h.confirmSignal && !h.rejectSignal && h.expect && (
                            <Text fz="xs"><Text span c="dimmed" fw={600}>ожидаем:</Text> {h.expect}</Text>
                        )}
                    </Stack>
                )}
                {h.verdict && <Text fz="xs"><Text span fw={600}>вердикт:</Text> {h.verdict}</Text>}
                <Group gap={4} wrap="wrap">
                    {h.checks.map(c   => <EntityChip key={'c' + c} type="Предложение (VPC)" name={c} prefix="🔧" />)}
                    {h.moves.map(m    => <EntityChip key={'m' + m} type="Показатель" name={m} prefix="📊" />)}
                    {h.channels.map(c => <EntityChip key={'ch' + c} type="Канал" name={c} prefix="📣" />)}
                    {h.segments.map(s => <EntityChip key={'s' + s} type="Сегмент" name={s} prefix="👥" />)}
                </Group>
            </Stack>
        </Card>
    );
}

function IterationCard({ it }: { it: PlanBoardIteration }): JSX.Element {
    const openCard = useUiStore(s => s.openCard);
    const over = it.capacity !== undefined && it.focus.length > it.capacity;
    return (
        <Card withBorder padding="sm" radius="md">
            <Stack gap={6}>
                <Group justify="space-between" wrap="nowrap">
                    <Text fz="sm" fw={600} style={{ cursor: 'pointer' }} onClick={() => openCard({ type: 'Итерация', name: it.name })}>
                        {it.name}
                    </Text>
                    <Group gap={4} wrap="nowrap">
                        {it.size   && <Badge size="xs" variant="outline" style={{ textTransform: 'none' }}>{it.size}</Badge>}
                        {it.status && <Badge size="sm" variant="light" color={it.status === 'идёт' ? 'blue' : it.status === 'закрыта' ? 'gray' : 'yellow'} style={{ textTransform: 'none' }}>{it.status}</Badge>}
                    </Group>
                </Group>
                {it.goal && <Text fz="xs" c="dimmed">{it.goal}</Text>}
                <Group gap={4} wrap="wrap">
                    {it.focus.map(f => <EntityChip key={f} type="Гипотеза" name={f} prefix="🔬" />)}
                </Group>
                <Text fz="xs" c={over ? 'red.7' : 'dimmed'}>
                    фокус {it.focus.length}{it.capacity !== undefined ? ` / ёмкость ${it.capacity}` : ''}{over ? ' — перегруз' : ''}
                </Text>
                {it.outcome && <Text fz="xs"><Text span fw={600}>итог:</Text> {it.outcome}</Text>}
            </Stack>
        </Card>
    );
}

function FitCard({ fit }: { fit: PlanBoardFit }): JSX.Element {
    const openCard = useUiStore(s => s.openCard);
    return (
        <Card withBorder padding="sm" radius="md">
            <Stack gap={6}>
                <Group justify="space-between" wrap="nowrap">
                    <Text fz="xs" fw={600} c="dimmed" tt="uppercase" style={{ letterSpacing: 0.5 }}>
                        {FIT_KIND_TITLE[fit.kind ?? ''] ?? fit.kind ?? 'fit'}
                    </Text>
                    {fit.status && (
                        <Badge size="sm" variant="light" color={FIT_STATUS_COLOR[fit.status] ?? 'gray'} style={{ textTransform: 'none' }}>
                            {fit.status}
                        </Badge>
                    )}
                </Group>
                <Text fz="sm" fw={500} style={{ cursor: 'pointer' }} onClick={() => openCard({ type: 'Соответствие', name: fit.name })}>
                    {fit.name}
                </Text>
                {fit.between.length > 0 && (
                    <Text fz="xs" c="dimmed">{fit.between.join(' × ')}</Text>
                )}
                <Group gap={4} wrap="wrap">
                    {fit.checkedBy.map(hyp => <EntityChip key={hyp} type="Гипотеза" name={hyp} prefix="🔬" />)}
                </Group>
            </Stack>
        </Card>
    );
}

// ─── Кликабельные чипы ───────────────────────────────────────────────────

function EntityChip({ type, name, prefix }: { type: string; name: string; prefix?: string }): JSX.Element {
    const openCard = useUiStore(s => s.openCard);
    return (
        <Badge
            size="sm"
            variant="dot"
            style={{ cursor: 'pointer', textTransform: 'none', maxWidth: 260 }}
            onClick={() => openCard({ type, name })}
            title={`${type} «${name}»`}
        >
            {prefix ? `${prefix} ` : ''}{name}
        </Badge>
    );
}

function ChipOpener({ type, name, label }: { type: string; name: string; label: string }): JSX.Element {
    const openCard = useUiStore(s => s.openCard);
    return (
        <Text component="span" fz="xs" style={{ cursor: 'pointer' }} onClick={() => openCard({ type, name })}>
            {label}
        </Text>
    );
}
